import { WelcomeForm } from '@features/onboarding/components/1-welcome-form/WelcomeForm';
import { ApplicantsForm } from '@features/onboarding/components/2-applicants-form/ApplicantsForm';
import { MedicalPlansForm } from '@features/onboarding/components/3-medical-plans-form/MedicalPlansForm';
import { DentalAndVisionForm } from '@features/onboarding/components/4-dental-and-vision-form/DentalAndVisionForm';
// import { SummaryForm } from '@features/onboarding/components/5-beyond-health-form/SummaryForm';
import { HealthQuestionForm } from '@features/onboarding/components/6-health-question-form/HealthQuestionForm';
import { Finish } from '@features/onboarding/components/7-finish/Finish';
import { FormStepSections, FormStepID } from '@features/onboarding/enums/formSections.enum';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useApplicationStore } from 'src/store/application';
import { useGeneralStore } from 'src/store';

export interface FormStep {
  id: string;
  number: number;
  title: string;
  sectionName: string;
  description?: string;
  subscription?: string;
  disabled?: boolean;
  image?: string;
  showSummary?: boolean;
  Component: React.ComponentType<FormStepProps>;
}

export const useFormSteps: () => FormStep[] = () => {
  const { data } = useOnboardingStore();
  const medicalPlan = data?.medicalPlan;
  const { signedAt } = useApplicationStore();
  const { siteConfig } = useGeneralStore();

  const steps: FormStep[] = [
    {
      id: FormStepID.Welcome,
      number: 1,
      showSummary: false,
      sectionName: FormStepSections.Welcome,
      title: siteConfig?.welcomeTitle || '',
      description: siteConfig?.welcomeDescription.replace(/\\n/g, '\n') || '',
      Component: WelcomeForm,
    },
    {
      id: FormStepID.Dependents,
      number: 2,
      showSummary: false,
      sectionName: FormStepSections.Dependents,
      title: 'A little information about you and your dependents',
      description:
        'On this page, you’ll enter details about yourself and any dependents who should be covered under your benefits. Once added, you can include or remove dependents from any of the plans you choose.',
      Component: ApplicantsForm,
    },
    {
      id: FormStepID.HealthPlans,
      number: 3,
      sectionName: FormStepSections.Medical,
      title: 'Health Plans',
      description:
        'Welcome to your Plans page! Picking the right plan is one of the most important steps you can take for your health and your wallet—and we’re here to make it simple. We offer a range of options, from affordable coverage to more comprehensive plans. Explore at your own pace, and if you’d like help comparing or running “what if” scenarios, our AI assistant Alia is just a click away.',
      subscription:
        '<strong>A quick note about premium quotes</strong><br />Your final quote may be different than the quotes shown on this page. Once you complete the application, we’ll provide a personalized quote with your exact monthly premium.',
      Component: MedicalPlansForm,
    },
    {
      id: FormStepID.VisionPlans,
      number: 4,
      sectionName: FormStepSections.Dental,
      title: 'Dental plans',
      description:
        'Taking care of your smile is more than just brushing and flossing—it’s about having the right coverage when you need it. That’s why we offer two dental plans designed to fit different needs and budgets. Whether you’re looking for essential coverage for routine checkups or more comprehensive protection that includes major procedures, we’ve got you covered. A good dental plan can help you stay on top of your oral health while avoiding unexpected costs.',
      Component: DentalAndVisionForm,
    },
    // {
    //   id: 'BeyondHealth',
    //   number: 5,
    //   sectionName: FormStepSections.BeyondHealth,
    //   title: 'Beyond Health',
    //   description:
    //     'Health insurance is a great start—but it’s not the whole picture. That’s why we offer additional coverage options that help protect other important parts of your life. From identity theft protection and legal plans to pet insurance and renters insurance, these add-ons can give you extra peace of mind when life throws the unexpected your way. Take a look—you might find something that fits just right!',
    //   Component: SummaryForm,
    // },
  ];
  if (medicalPlan) {
    steps.push({
      id: FormStepID.PHQ,
      number: 5,
      sectionName: FormStepSections.HealthQuestion,
      title: 'Your personal health questionnaire (phq)',
      description:
        'Because you have selected a medical plan we have to ask you a few quick health questions to get to know you better. No worries, it’ll only take a few minutes! After that, we can instantly let you know if you are accepted for coverage and, if you are, we can give you an exact price for the plan of your choice.',
      Component: HealthQuestionForm,
    });
  }
  steps.push({
    id: 'Finish',
    number: steps.length + 1,
    sectionName: FormStepSections.Finish,
    title: 'Congratulations!',
    description:
      'Congratulations! You’ve been approved for your medical insurance, and your final premium is locked in —great job taking this important step toward protecting your health. If you ever want to make changes to the plans you selected, no problem!',
    Component: Finish,
  });

  return steps.filter(e => {
    if (e.id === FormStepID.PHQ && signedAt) {
      return false;
    }
    return true;
  });
};
